import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./Login";
import AdminPanel from "./components/admin/AdminPanel";
import EmpleadoPanel from "./components/empleado/EmpleadoPanel";
import EmpleadoAusencias from "./pages/EmpleadoAusencias";
import Profile from "./pages/Profile";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastProvider } from "./components/ui/ToastContext";
import ForgotPasswordForm from "./components/auth/ForgotPasswordForm";
import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import AppFooter from "./pages/AppFooter";

import { getAccessToken, doLogout } from "./services/api";

// ⬇️ NUEVO: página legal
import LegalPage from "./pages/LegalPage";

// --- helpers persistencia ---
const ROLE_KEY = "role";
const USER_KEY = "user";
const LAST_VIEW_KEY = "lastView"; // ← recordamos a dónde quiere caer el admin (/admin|/empleado)

function getSaved(key) {
  try {
    return (
      (typeof localStorage !== "undefined" && localStorage.getItem(key)) ??
      (typeof sessionStorage !== "undefined" && sessionStorage.getItem(key)) ??
      null
    );
  } catch {
    return null;
  }
}
function setSaved(key, value) {
  try {
    const useLocal =
      typeof localStorage !== "undefined" &&
      localStorage.getItem("access") != null;
    if (useLocal && typeof localStorage !== "undefined") {
      localStorage.setItem(key, value);
    } else if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(key, value);
    }
  } catch {}
}
function clearSaved(keys) {
  try {
    for (const k of keys) {
      if (typeof localStorage !== "undefined") localStorage.removeItem(k);
      if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(k);
    }
  } catch {}
}

// Componente pequeño para decidir a dónde cae la raíz "/" para admins
function AdminHomeRedirect() {
  let dest = "/admin";
  try {
    const v = localStorage.getItem(LAST_VIEW_KEY);
    if (v === "/empleado" || v === "/admin") dest = v;
  } catch {}
  return <Navigate to={dest} replace />;
}

export default function App() {
  const [session, setSession] = useState(null);

  // Rehidratación
  useEffect(() => {
    const token =
      getAccessToken() ||
      (typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem("token"));

    if (!token) return;

    const roleSaved = (getSaved(ROLE_KEY) || "").toLowerCase();
    const userSaved = getSaved(USER_KEY);

    if (roleSaved) {
      setSession({ token, role: roleSaved, user: userSaved });
      return;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1] || ""));
      const role = (payload.role || "employee").toLowerCase();
      const user = payload.sub || null;
      setSession({ token, role, user });
    } catch {
      try {
        if (typeof sessionStorage !== "undefined")
          sessionStorage.removeItem("token");
      } catch {}
    }
  }, []);

  // Desde <Login />
  const handleLogin = (s) => {
    const token = getAccessToken();
    if (!token) {
      setSession(null);
      return;
    }
    const role = (s?.role || "employee").toLowerCase();
    const user = s?.user || null;

    setSaved(ROLE_KEY, role);
    if (user) setSaved(USER_KEY, user);

    setSession({ token, role, user });
  };

  const handleLogout = async () => {
    await doLogout();
    clearSaved([ROLE_KEY, USER_KEY, LAST_VIEW_KEY]);
    setSession(null);
  };

  const isAdmin = (session?.role || "").toLowerCase() === "admin";

  return (
    <ToastProvider>
      <BrowserRouter>
        <div
          className="min-h-screen"
          style={{
            backgroundImage: "url('/fondo.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
          }}
        >
          <Routes>
            {/* 🔓 RUTA PÚBLICA SIEMPRE ACCESIBLE */}
            <Route path="/legal" element={<LegalPage />} />

            {session ? (
              <>
                {/* Comunes con sesión */}
                <Route
                  path="/profile"
                  element={<Profile session={session} onLogout={handleLogout} />}
                />
                <Route path="/forgot" element={<ForgotPasswordForm />} />
                <Route path="/reset" element={<ResetPasswordPage />} />

                {isAdmin ? (
                  <>
                    {/* Admin tiene ambas vistas */}
                    <Route
                      path="/admin"
                      element={<AdminPanel session={session} onLogout={handleLogout} />}
                    />
                    <Route
                      path="/empleado"
                      element={<EmpleadoPanel session={session} onLogout={handleLogout} />}
                    />
                    <Route
                      path="/ausencias"
                      element={<EmpleadoAusencias session={session} />}
                    />

                    {/* raíz decide por última vista usada */}
                    <Route path="/" element={<AdminHomeRedirect />} />
                    {/* cualquier otra → raíz (que a su vez decide) */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </>
                ) : (
                  <>
                    {/* Empleado normal */}
                    <Route
                      path="/"
                      element={<EmpleadoPanel session={session} onLogout={handleLogout} />}
                    />
                    <Route
                      path="/ausencias"
                      element={<EmpleadoAusencias session={session} />}
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </>
                )}
              </>
            ) : (
              <>
                {/* Público */}
                <Route path="/login" element={<Login onLogin={handleLogin} />} />
                <Route path="/forgot" element={<ForgotPasswordForm />} />
                <Route path="/reset" element={<ResetPasswordPage />} />
                {/* Mantén este catch-all. No bloquea /legal porque está declarada arriba */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            )}
          </Routes>
          <AppFooter/>
        </div>

        <ToastContainer position="top-right" autoClose={3000} />
      </BrowserRouter>
    </ToastProvider>
  );
}
